import FAQAccordion from "@/components/FAQAccordion";
import { ReferenceTable } from "@/components/ReferenceTable";
import { REF_YOGINI_EN } from "@/lib/reference-tables.generated";
import type { FAQItem } from "@/lib/schema";

export const YOGINI_FAQS: FAQItem[] = [
  {
    question: "What is Yogini Dasha in Vedic astrology?",
    answer:
      "Yogini Dasha is an 8-fold planetary cycle described in Chapter 47 of the Brihat Parashara Hora Shastra. Unlike the 120-year Vimshottari Dasha cycle, the Yogini system spans exactly 36 years across 8 Yoginis: Mangala (1 yr, Moon), Pingala (2 yrs, Sun), Dhanya (3 yrs, Jupiter), Bhramari (4 yrs, Mars), Bhadrika (5 yrs, Mercury), Ulka (6 yrs, Saturn), Siddha (7 yrs, Venus), and Sankata (8 yrs, Rahu). It is renowned for rapid, decisive timing of life events.",
  },
  {
    question: "How is the starting Yogini determined?",
    answer:
      "The starting Yogini is determined by your Moon nakshatra at birth using the classical formula: (Nakshatra Number + 3) mod 8. If the remainder is 1 it starts with Mangala, 2 with Pingala, 3 with Dhanya, 4 with Bhramari, 5 with Bhadrika, 6 with Ulka, 7 with Siddha, and 0/8 with Sankata. The elapsed portion of the Moon in that nakshatra determines the balance of the first Yogini dasha.",
  },
  {
    question: "How does Yogini Dasha differ from Vimshottari Dasha?",
    answer:
      "Vimshottari Dasha spans 120 years and uses 9 grahas, making it a comprehensive life-long blueprint. Yogini Dasha spans 36 years and cycles 3 times in a standard 108-year lifetime. Because the periods are shorter (1 to 8 years), Yogini Dasha reflects tangible shifts, career milestones, and domestic transformations with intense clarity.",
  },
  {
    question: "Which Yoginis are auspicious and which are challenging?",
    answer:
      "Mangala (Moon), Dhanya (Jupiter), Bhadrika (Mercury), and Siddha (Venus) are traditionally considered Shubha (auspicious) Yoginis associated with prosperity, knowledge, and fulfillment. Pingala (Sun), Bhramari (Mars), Ulka (Saturn), and Sankata (Rahu) are Ashubha (challenging) Yoginis calling for discipline, patience, and remedial Vedic measures.",
  },
  {
    question: "Can Yogini Dasha be used alongside Vimshottari?",
    answer:
      "Yes. Classical Jyotish masters recommend cross-verifying major life predictions between Vimshottari Dasha and Yogini Dasha. When both systems indicate a favorable period (e.g. Vimshottari Guru Mahadasha coinciding with Yogini Dhanya or Siddha), the positive result manifests with double certainty.",
  },
];

export const YOGINI_FAQS_HI: FAQItem[] = [
  {
    question: "वैदिक ज्योतिष में योगिनी दशा क्या है?",
    answer:
      "योगिनी दशा वृहत् पराशर होरा शास्त्र (अध्याय 47) में वर्णित एक अत्यंत सटीक 8-चरणीय दशा प्रणाली है। विंशोत्तरी के 120 वर्ष की तुलना में इसका कुल चक्र 36 वर्ष का होता है: मंगला (1 वर्ष, चन्द्र), पिंगला (2 वर्ष, सूर्य), धान्या (3 वर्ष, गुरु), भ्रामरी (4 वर्ष, मंगल), भद्रिका (5 वर्ष, बुध), उल्का (6 वर्ष, शनि), सिद्धा (7 वर्ष, शुक्र), और संकटा (8 वर्ष, राहु)।",
  },
  {
    question: "प्रारंभिक योगिनी की गणना कैसे होती है?",
    answer:
      "जन्म के समय चंद्रमा के नक्षत्र संख्या में 3 जोड़कर 8 से भाग दिया जाता है: (नक्षत्र संख्या + 3) mod 8। शेषफल के अनुसार प्रारंभिक योगिनी निर्धारित होती है। नक्षत्र में चंद्रमा के भुक्त अंश से प्रथम योगिनी का भुक्त/भोग्य शेष निकाला जाता है।",
  },
  {
    question: "शुभ और अशुभ योगिनी कौन सी हैं?",
    answer:
      "मंगला (चन्द्र), धान्या (गुरु), भद्रिका (बुध) एवं सिद्धा (शुक्र) शुभ योगिनी मानी जाती हैं जो सुख, समृद्धि एवं आध्यात्मिक सिद्धि देती हैं। पिंगला (सूर्य), भ्रामरी (मंगल), उल्का (शनि) एवं संकटा (राहु) चुनौतीपूर्ण योगिनी मानी जाती हैं जिनमें संयम और वैदिक उपाय आवश्यक होते हैं।",
  },
  {
    question: "क्या योगिनी दशा को विंशोत्तरी के साथ देखा जाना चाहिए?",
    answer:
      "हाँ, शास्त्रीय परंपरा में किसी भी बड़े निर्णय या घटना के समय-निर्धारण के लिए विंशोत्तरी और योगिनी दोनों दशाओं का मिलान किया जाता है। जब दोनों दशाएं एक ही शुभ संकेत दें, तो फल की प्राप्ति निश्चित मानी जाती है।",
  },
];

export function YoginiDashaSeoContent({ locale = "en" }: { locale?: "en" | "hi" }) {
  const faqs = locale === "hi" ? YOGINI_FAQS_HI : YOGINI_FAQS;

  return (
    <section
      className="mx-auto max-w-2xl px-4 py-12 text-text-secondary sm:px-6 sm:py-16"
      data-design-owner="yogini-dasha-editorial"
    >
      <h2 className="mb-4 font-serif text-title font-bold text-text-primary">
        {locale === "hi"
          ? "वैदिक ज्योतिष में 8 योगिनियों का महत्व"
          : "Understanding the 8 Yoginis in Vedic Astrology"}
      </h2>
      <p className="mb-6 leading-relaxed text-text-secondary">
        {locale === "hi"
          ? "योगिनी दशा नक्षत्र-आधारित दशा प्रणालियों में अत्यंत महत्वपूर्ण है। यह 36 वर्षों में अपना पूरा चक्र समाप्त करती है, जिससे जीवन के महत्वपूर्ण मोड़ों और दशा परिवर्तनों का स्पष्ट बोध होता है।"
          : "The Yogini Dasha is one of the most revered Nakshatra-based dasha systems in classical Parashari Jyotish. While Vimshottari Dasha unfolds over 120 years, Yogini Dasha completes its entire 8-step cycle in 36 years."}
      </p>

      <ReferenceTable
        dataset={REF_YOGINI_EN}
        heading="The 8 Yoginis — Ruling Grahas & Years"
        intro="The engine-owned Yogini sequence and its fixed periods:"
        minWidthClass="min-w-[640px]"
      />

      <div className="mt-10 mb-6">
        <h3 className="text-xl font-bold font-serif text-text-primary mb-3">
          {locale === "hi"
            ? "सक्रिय योगिनी दशा कैसे समझें"
            : "How to Read Your Active Yogini Dasha"}
        </h3>
        <p className="text-sm leading-relaxed text-text-secondary mb-3">
          1. <strong>Starting Yogini:</strong>{" "}
          {locale === "hi"
            ? "जन्म के समय चंद्रमा के नक्षत्र से निर्धारित।"
            : "Computed from your Moon nakshatra at the moment of birth."}
        </p>
        <p className="text-sm leading-relaxed text-text-secondary mb-3">
          2. <strong>Balance of Dasha:</strong>{" "}
          {locale === "hi"
            ? "नक्षत्र में चंद्रमा के भुक्त अंश से प्रथम दशा का शेष काल।"
            : "The exact degree the Moon had traveled inside its nakshatra decides how many months remain at birth."}
        </p>
        <p className="text-sm leading-relaxed text-text-secondary mb-3">
          3. <strong>Sequential Cycles:</strong>{" "}
          {locale === "hi"
            ? "36 वर्षों के चक्र 108 वर्ष की आयु तक 3 बार दोहराए जाते हैं।"
            : "Repeats across 3 sequential 36-year cycles for a 108-year timeline."}
        </p>
      </div>

      <FAQAccordion
        title={locale === "hi" ? "अक्सर पूछे जाने वाले प्रश्न" : "Frequently Asked Questions"}
        items={faqs}
      />
    </section>
  );
}
